import React, { useState, useEffect, useCallback, useRef } from "react";
import env from "../../config/env.js";
import { getHeaders } from "../../utils/apiUtils";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, TableSortLabel,
  Tooltip, Select, MenuItem, InputLabel, FormControl,
  Snackbar, Alert, TablePagination, CircularProgress, Grid, Card, CardContent,
  Tabs, Tab
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  BarChart as BarChartIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon
} from "@mui/icons-material";

// Recharts Components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";

const diasSemana = [
  "Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"
];


const normalizarHora = (valor) => {
  if (!valor) return '';
  const partes = valor.split(':');
  if (partes.length >= 2) {
    const hora = partes[0]?.padStart(2, '0') ?? '00';
    const minuto = partes[1]?.padStart(2, '0') ?? '00';
    return `${hora}:${minuto}`;
  }
  return valor;
};

export default function DispSemanalColab() {
  // Hook do sistema de mensagens
  const { addError } = useSystemMessagesContext();

  const [dispSelecionada, setDispSelecionada] = useState(null);
  const [dispVisualizar, setDispVisualizar] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("colaborador");
  const [order, setOrder] = useState("asc");
  const [filtros, setFiltros] = useState({
    colaborador: '',
    dia: ''
  });
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState('');
  const [openGrafico, setOpenGrafico] = useState(false);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());
  const [formData, setFormData] = useState({
    id_colaborador: '',
    dia: '',
    inicio: '',
    fim: '',
    observacoes: '',
    ativo: true
  });

  const colaboradoresCarregadosRef = useRef(false);

  const carregarColaboradores = useCallback(async () => {
    if (colaboradoresCarregadosRef.current) {
      return;
    }

    try {
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      
      const headers = getHeaders();

      const res = await api.get('/colaboradores', { 
        headers,
        params: {
          st_codHashEmpresa: codHashEmpresa
        }
      });
      
      let colaboradoresFiltrados = res.data;
      if (usuario.perfil !== "superusuario") {
        colaboradoresFiltrados = res.data.filter(c => c.st_codHashEmpresa === codHashEmpresa);
      }
      
      setColaboradores(colaboradoresFiltrados);
      colaboradoresCarregadosRef.current = true;
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  }, []);

  useEffect(() => {
    carregarColaboradores();
  }, [carregarColaboradores]);

  // Buscar disponibilidades
  const disponibilidadesCarregadasRef = useRef(false);

  const carregarDisponibilidades = useCallback(async (options = {}) => {
    const { force = false } = options;

    if (!force && disponibilidadesCarregadasRef.current) {
      return;
    }

    setLoading(true);
    try {
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = getHeaders();

      const res = await api.get('/disponibilidade', { 
        headers,
        params: {
          st_codHashEmpresa: codHashEmpresa
        }
      });
      
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      let disponibilidadesFiltradas = res.data;
      
      if (usuario.perfil !== "superusuario") {
        disponibilidadesFiltradas = res.data.filter(disp => 
          disp.st_codHashEmpresa === codHashEmpresa
        );
      }
      
      setDisponibilidades(disponibilidadesFiltradas);
      disponibilidadesCarregadasRef.current = true;
    } catch (err) {
      addError((err.response && err.response.data && err.response.data.message) || 'Erro ao carregar disponibilidades');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    carregarDisponibilidades({ force: false });
  }, [carregarDisponibilidades]);

  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: false }));
  };

  // Função para formatar hora no padrão HH:MM
  const handleHorarioChange = (name, value) => {
    // Remove caracteres não numéricos
    const numeros = value.replace(/\D/g, '');
    
    // Limita a 4 dígitos (HHMM)
    if (numeros.length > 4) return;
    
    // Formata como HH:MM
    let horaFormatada = numeros;
    if (numeros.length >= 2) {
      const hora = numeros.substring(0, 2);
      const minuto = numeros.substring(2, 4);
      
      // Valida hora (00-23)
      const horaNum = parseInt(hora);
      if (horaNum > 23) return;
      
      // Valida minuto (00-59)
      if (minuto && parseInt(minuto) > 59) return;
      
      horaFormatada = `${hora}${minuto ? ':' + minuto : ''}`;
    }
    
    setFormData(prev => ({ ...prev, [name]: horaFormatada }));
    setFormErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleOpenForm = (disp = null) => {
    if (disp) {
      setFormData({
        id_colaborador: disp.id_colaborador,
        dia: disp.dia,
        inicio: normalizarHora(disp.inicio),
        fim: normalizarHora(disp.fim),
        observacoes: disp.observacoes || '',
        ativo: disp.ativo !== undefined ? disp.ativo : true
      });
      setDispSelecionada(disp);
    } else {
      setFormData({ 
        id_colaborador: '', 
        dia: '', 
        inicio: '', 
        fim: '',
        observacoes: '',
        ativo: true
      });
      setDispSelecionada(null);
    }
    setActiveTab(0);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({ 
      id_colaborador: '', 
      dia: '', 
      inicio: '', 
      fim: '',
      observacoes: '',
      ativo: true
    });
    setDispSelecionada(null);
    setFormErrors({});
  };

  const handleFormDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    handleCloseForm();
  };

  const visualizarDisponibilidade = (disp) => {
    setDispVisualizar(disp);
    setActiveTab(0);
    setOpenView(true);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setDispVisualizar(null);
  };

  const handleViewDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    fecharVisualizacao();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDelete = (disp) => {
    setDispSelecionada(disp);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDispSelecionada(null);
  };

  const handleDeleteDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    handleCloseDelete();
  };

  const handleOpenPrintDialog = () => {
    setOpenPrintDialog(true);
  };

  const handleClosePrintDialog = () => {
    setOpenPrintDialog(false);
    setColaboradorSelecionado('');
  };

  // Validação
  const validar = () => {
    const errors = {};
    if (!formData.id_colaborador) errors.id_colaborador = true;
    if (formData.dia === '' || formData.dia === null) errors.dia = true;
    if (!formData.inicio) errors.inicio = true;
    if (!formData.fim) errors.fim = true;

    // Validar formato de hora (HH:MM)
    const horaRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (formData.inicio && !horaRegex.test(formData.inicio)) {
      errors.inicio = true;
      addError('Horário de início deve estar no formato HH:MM');
      return false;
    }
    if (formData.fim && !horaRegex.test(formData.fim)) {
      errors.fim = true;
      addError('Horário de fim deve estar no formato HH:MM');
      return false;
    }

    // Validar se o horário de fim é maior que o de início
    if (formData.inicio && formData.fim) {
      const [horaInicio, minutoInicio] = formData.inicio.split(':').map(Number);
      const [horaFim, minutoFim] = formData.fim.split(':').map(Number);
      
      const totalMinutosInicio = horaInicio * 60 + minutoInicio;
      const totalMinutosFim = horaFim * 60 + minutoFim;
      
      if (totalMinutosFim <= totalMinutosInicio) {
        errors.fim = true;
        addError('O horário de fim deve ser maior que o horário de início');
        return false;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar (POST/PUT)
  const handleSalvar = async () => {
    if (!validar()) return;
    setLoading(true);
    try {
      const headers = getHeaders();

      const dados = {
        id_colaborador: parseInt(formData.id_colaborador),
        dia: parseInt(formData.dia),
        inicio: formData.inicio,
        fim: formData.fim
      };

      if (dispSelecionada) {
        // Atualização (PUT)
        await api.put(`/disponibilidade/${dispSelecionada.id}`, dados, { headers });
        setSuccess("Disponibilidade atualizada com sucesso!");
      } else {
        // Criação (POST)
        await api.post('/disponibilidade', dados, { headers });
        setSuccess("Disponibilidade criada com sucesso!");
      }

      handleCloseForm();
      carregarDisponibilidades({ force: true });
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || 'Erro ao salvar disponibilidade');
    } finally {
      setLoading(false);
    }
  };

  // Excluir
  const handleExcluir = async () => {
    if (!dispSelecionada) return;
    setLoading(true);
    try {
      const headers = getHeaders();
      
      await api.delete(`/disponibilidade/${dispSelecionada.id}`, { headers });
      setSuccess("Disponibilidade excluída com sucesso!");
      handleCloseDelete();
      carregarDisponibilidades({ force: true });
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || 'Erro ao excluir disponibilidade');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  // Função para aplicar filtros
  const disponibilidadesFiltradas = disponibilidades.filter(disp => {
    const colaborador = colaboradores.find(c => c.id === disp.id_colaborador);
    const nomeColaborador = (colaborador && colaborador.nome_completo && colaborador.nome_completo.toLowerCase()) || '';
    const filtroColaborador = filtros.colaborador.toLowerCase();
    const filtroDia = filtros.dia;

    return (
      (filtroColaborador === '' || nomeColaborador.includes(filtroColaborador)) &&
      (filtroDia === '' || disp.dia === parseInt(filtroDia))
    );
  });

  // Ordenação
  const disponibilidadesOrdenadas = disponibilidadesFiltradas.sort((a, b) => {
    const isAsc = order === "asc";
    if (orderBy === "colaborador") {
      const nomeA = (colaboradores.find(c => c.id === a.id_colaborador) && colaboradores.find(c => c.id === a.id_colaborador).nome_completo) || '';
      const nomeB = (colaboradores.find(c => c.id === b.id_colaborador) && colaboradores.find(c => c.id === b.id_colaborador).nome_completo) || '';
      return isAsc ? nomeA.localeCompare(nomeB) : nomeB.localeCompare(nomeA);
    }
    if (orderBy === "dia") {
      return isAsc ? a.dia - b.dia : b.dia - a.dia;
    }
    if (orderBy === "inicio") {
      return isAsc ? a.inicio.localeCompare(b.inicio) : b.inicio.localeCompare(a.inicio);
    }
    if (orderBy === "fim") {
      return isAsc ? a.fim.localeCompare(b.fim) : b.fim.localeCompare(a.fim);
    }
    return 0;
  });

  // Paginação
  const disponibilidadesPaginadas = disponibilidadesOrdenadas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Função para processar dados do gráfico de ocupação
  const processarDadosGrafico = () => {
    if (!disponibilidades.length) return [];

    // Criar slots de hora para cada dia (00:00 até 23:00)
    const slotsHora = {};
    diasSemana.forEach((dia, idx) => {
      slotsHora[idx] = {};
      for (let hora = 0; hora < 24; hora++) {
        slotsHora[idx][hora] = {
          hora: `${hora.toString().padStart(2, '0')}:00`,
          ocupacao: 0,
          colaboradores: []
        };
      }
    });

    // Processar cada disponibilidade
    disponibilidades.forEach(disp => {
      const inicio = parseInt(disp.inicio.split(':')[0]);
      const fim = parseInt(disp.fim.split(':')[0]);
      const colaborador = colaboradores.find(c => c.id === disp.id_colaborador);
      
      // Para cada hora no intervalo da disponibilidade
      for (let hora = inicio; hora < fim; hora++) {
        if (slotsHora[disp.dia] && slotsHora[disp.dia][hora]) {
          slotsHora[disp.dia][hora].ocupacao++;
          if (colaborador) {
            slotsHora[disp.dia][hora].colaboradores.push(colaborador.nome_completo);
          }
        }
      }
    });

    // Converter para formato do gráfico
    const dadosGrafico = [];
    diasSemana.forEach((dia, idx) => {
      const dadosDia = {
        dia: dia,
        diaIndex: idx
      };
      
      // Adicionar dados de ocupação para cada hora
      for (let hora = 0; hora < 24; hora++) {
        const slot = slotsHora[idx][hora];
        dadosDia[`hora_${hora}`] = slot.ocupacao;
      }
      
      dadosGrafico.push(dadosDia);
    });

    return dadosGrafico;
  };

  // Função para abrir o gráfico
  const abrirGrafico = () => {
    const dados = processarDadosGrafico();
    setDadosGrafico(dados);
    setOpenGrafico(true);
  };

  // Função para fechar o gráfico
  const fecharGrafico = () => {
    setOpenGrafico(false);
  };

  const imprimirDisponibilidades = () => {
    if (!colaboradorSelecionado) return;

    const dataAtual = new Date().toLocaleString('pt-BR');
    const empresa = localStorage.getItem("st_nomeEmpresa") || "Empresa";
    const colaborador = colaboradores.find(c => c.id === colaboradorSelecionado);
    
    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');
    
    // Estilo CSS para a impressão
    const styles = `
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
        }
        .title {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .subtitle {
          font-size: 14px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .footer {
          margin-top: 20px;
          text-align: right;
          font-size: 12px;
          color: #666;
        }
        @media print {
          .no-print {
            display: none;
          }
        }
      </style>
    `;

    // Filtrar disponibilidades do colaborador selecionado
    const disponibilidadesColaborador = disponibilidades.filter(
      disp => disp.id_colaborador === colaboradorSelecionado
    );

    // Conteúdo HTML para impressão
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Disponibilidades do Colaborador</title>
          ${styles}
        </head>
        <body>
          <div class="header">
            <div class="title">Disponibilidades do Colaborador</div>
            <div class="subtitle">${empresa}</div>
            <div class="subtitle">Colaborador: ${(colaborador && colaborador.nome_completo) || colaboradorSelecionado}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Dia</th>
                <th>Início</th>
                <th>Fim</th>
              </tr>
            </thead>
            <tbody>
              ${disponibilidadesColaborador.map(disp => `
                <tr>
                  <td>${diasSemana[disp.dia]}</td>
                  <td>${disp.inicio}</td>
                  <td>${disp.fim}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Gerado em: ${dataAtual}</p>
          </div>
          
          <div class="no-print">
            <button onclick="window.print()">Imprimir</button>
          </div>
        </body>
      </html>
    `;

    // Escrever o conteúdo na nova janela
    printWindow.document.write(content);
    printWindow.document.close();
    handleClosePrintDialog();
  };

  return (
    <AcessoRestrito modulo="dispsemanalcolab">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Notificações */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!success} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        
        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>

          {/* Header */}
          <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            pb: 2,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <ScheduleIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }} />
              Disponibilidade Semanal dos Colaboradores
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                onClick={abrirGrafico}
                sx={{ 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  borderColor: (theme) => theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderColor: (theme) => theme.palette.primary.dark
                  }
                }}
              >
                📊 Gráfico de Ocupação
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handleOpenPrintDialog}
              >
                Imprimir
              </Button>
              <Button 
                variant="contained" 
                onClick={() => handleOpenForm()}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: '200px' },
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#000000' 
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? '#f5f5f5' 
                      : theme.palette.primary.dark,
                  }
                }}
              >
                <AddIcon /> Nova Disponibilidade
              </Button>
            </Box>
          </Box>

          {/* Container da Tabela */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: '100%',
              maxHeight: '100%',
              width: '100%',
              minWidth: '100%',
              maxWidth: '100%',
              boxShadow: (theme) => theme.palette.mode === 'dark' 
                ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
                : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 1,
              border: 'none'
            }}
          >
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width="40%">
                  <TableSortLabel
                    active={orderBy === "colaborador"}
                    direction={orderBy === "colaborador" ? order : "asc"}
                    onClick={() => handleRequestSort("colaborador")}
                  >
                    Colaborador
                  </TableSortLabel>
                  <TextField
                    name="colaborador"
                    size="small"
                    fullWidth
                    value={filtros.colaborador}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por nome"
                  />
                </TableCell>
                <TableCell width="15%">
                  <TableSortLabel
                    active={orderBy === "dia"}
                    direction={orderBy === "dia" ? order : "asc"}
                    onClick={() => handleRequestSort("dia")}
                  >
                    Dia
                  </TableSortLabel>
                  <FormControl fullWidth size="small">
                    <Select
                      name="dia"
                      value={filtros.dia}
                      onChange={handleFiltroChange}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {diasSemana.map((dia, idx) => (
                        <MenuItem key={idx} value={idx}>{dia}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell width="15%">
                  <TableSortLabel
                    active={orderBy === "inicio"}
                    direction={orderBy === "inicio" ? order : "asc"}
                    onClick={() => handleRequestSort("inicio")}
                  >
                    Início
                  </TableSortLabel>
                </TableCell>
                <TableCell width="15%">
                  <TableSortLabel
                    active={orderBy === "fim"}
                    direction={orderBy === "fim" ? order : "asc"}
                    onClick={() => handleRequestSort("fim")}
                  >
                    Fim
                  </TableSortLabel>
                </TableCell>
                <TableCell width="15%">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : disponibilidadesPaginadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhuma disponibilidade encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                disponibilidadesPaginadas.map((disp) => (
                  <TableRow key={disp.id}>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(colaboradores.find(c => c.id === disp.id_colaborador) && colaboradores.find(c => c.id === disp.id_colaborador).nome_completo) || disp.id_colaborador}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {diasSemana[disp.dia]}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {disp.inicio}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {disp.fim}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => visualizarDisponibilidade(disp)}>
                            <VisibilityIcon fontSize="small" className="material-icon-visibility" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenForm(disp)}>
                            <EditIcon fontSize="small" className="material-icon-edit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleOpenDelete(disp)}>
                            <DeleteIcon fontSize="small" className="material-icon-delete" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

          {/* Paginação Fixa */}
          <TablePagination
            component="div"
            count={disponibilidadesFiltradas.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              width: '100%',
              px: 3,
              py: 1.5,
              boxSizing: 'border-box',
              zIndex: 1000,
              minHeight: '60px'
            }}
          />
        </Box>

        {/* Formulário */}
        <Dialog 
          open={openForm} 
          onClose={handleFormDialogClose} 
          maxWidth="md" 
          fullWidth
          disableEscapeKeyDown
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              overflowY: 'auto',
              width: '900px',
              height: '550px'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            {dispSelecionada ? "Editar Disponibilidade" : "Nova Disponibilidade"}
          </DialogTitle>
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px',
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem'
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem'
            }
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                mb: 2
              }}
            >
              <Tab label="Dados Básicos" />
              <Tab label="Horários" />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FormControl fullWidth error={formErrors.id_colaborador}>
                  <InputLabel>Colaborador</InputLabel>
                  <Select
                    name="id_colaborador"
                    value={formData.id_colaborador}
                    onChange={handleInputChange}
                    label="Colaborador"
                  >
                    {colaboradores.map(colab => (
                      <MenuItem key={colab.id} value={colab.id}>
                        {colab.nome_completo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth error={formErrors.dia}>
                  <InputLabel>Dia da Semana</InputLabel>
                  <Select
                    name="dia"
                    value={formData.dia}
                    onChange={handleInputChange}
                    label="Dia da Semana"
                  >
                    {diasSemana.map((dia, idx) => (
                      <MenuItem key={idx} value={idx}>{dia}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Horário de Início"
                  name="inicio"
                  type="text"
                  value={formData.inicio}
                  onChange={(e) => handleHorarioChange("inicio", e.target.value)}
                  error={formErrors.inicio}
                  placeholder="HH:MM"
                  helperText="Digite o horário no formato HH:MM (ex: 08:30)"
                  inputProps={{
                    maxLength: 5
                  }}
                />
                <TextField
                  fullWidth
                  label="Horário de Fim"
                  name="fim"
                  type="text"
                  value={formData.fim}
                  onChange={(e) => handleHorarioChange("fim", e.target.value)}
                  error={formErrors.fim}
                  placeholder="HH:MM"
                  helperText="Digite o horário no formato HH:MM (ex: 17:30)"
                  inputProps={{
                    maxLength: 5
                  }}
                />
              </Box>
            )}

          </DialogContent>
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& .MuiButton-root': {
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              fontWeight: 500
            },
            '& .MuiTypography-root': {
              fontSize: '0.875rem'
            }
          }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeTab > 0 && (
                <Button 
                  onClick={() => setActiveTab(activeTab - 1)}
                  variant="outlined"
                  size="small"
                >
                  Anterior
                </Button>
              )}
              {activeTab < 1 && (
                <Button 
                  onClick={() => setActiveTab(activeTab + 1)}
                  variant="outlined"
                  size="small"
                >
                  Próximo
                </Button>
              )}
              <Button 
                onClick={handleCloseForm}
                variant="contained" 
                color="primary"
              >
                FECHAR
              </Button>
              <Button 
                onClick={handleSalvar} 
                variant="contained" 
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "SALVAR"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Visualização */}
        <Dialog 
          open={openView} 
          onClose={handleViewDialogClose} 
          maxWidth="md" 
          fullWidth
          disableEscapeKeyDown
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              overflowY: 'auto',
              width: '900px',
              height: '550px'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Visualizar Disponibilidade
            <IconButton onClick={fecharVisualizacao} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px',
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem'
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem'
            }
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                mb: 2
              }}
            >
              <Tab label="Dados Básicos" />
              <Tab label="Horários" />
            </Tabs>

            {activeTab === 0 && dispVisualizar && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Colaborador"
                  value={colaboradores.find(c => c.id === dispVisualizar.id_colaborador)?.nome_completo || ''}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Dia da Semana"
                  value={diasSemana[dispVisualizar.dia] || ''}
                  disabled
                />
              </Box>
            )}

            {activeTab === 1 && dispVisualizar && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Horário de Início"
                  value={dispVisualizar.inicio}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Horário de Fim"
                  value={dispVisualizar.fim}
                  disabled
                />
              </Box>
            )}

          </DialogContent>
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& .MuiButton-root': {
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              fontWeight: 500
            },
            '& .MuiTypography-root': {
              fontSize: '0.875rem'
            }
          }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeTab > 0 && (
                <Button 
                  onClick={() => setActiveTab(activeTab - 1)}
                  variant="outlined"
                  size="small"
                >
                  Anterior
                </Button>
              )}
              {activeTab < 1 && (
                <Button 
                  onClick={() => setActiveTab(activeTab + 1)}
                  variant="outlined"
                  size="small"
                >
                  Próximo
                </Button>
              )}
              <Button 
                onClick={fecharVisualizacao}
                variant="contained" 
                color="primary"
              >
                FECHAR
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<PrintIcon />}
                onClick={() => {
                  // Implementar impressão da disponibilidade específica
                  console.log('Imprimir disponibilidade:', dispVisualizar);
                }}
              >
                IMPRIMIR
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Confirmação de exclusão */}
        <Dialog 
          open={openDelete} 
          onClose={handleDeleteDialogClose} 
          maxWidth="xs" 
          fullWidth
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2
          }}>
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            <Typography>
              Tem certeza que deseja excluir esta disponibilidade?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            padding: 2
          }}>
            <Button onClick={handleCloseDelete}>Cancelar</Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleExcluir} 
              disabled={loading}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de Seleção para Impressão */}
        <Dialog
          open={openPrintDialog}
          onClose={handleClosePrintDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2
          }}>
            Selecionar Colaborador para Impressão
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Colaborador</InputLabel>
              <Select
                value={colaboradorSelecionado}
                onChange={(e) => setColaboradorSelecionado(e.target.value)}
                label="Colaborador"
              >
                {colaboradores.map(colab => (
                  <MenuItem key={colab.id} value={colab.id}>
                    {colab.nome_completo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            padding: 2
          }}>
            <Button onClick={handleClosePrintDialog}>Cancelar</Button>
            <Button 
              variant="contained" 
              onClick={imprimirDisponibilidades}
              disabled={!colaboradorSelecionado}
            >
              Imprimir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog do Gráfico de Ocupação */}
        <Dialog
          open={openGrafico}
          onClose={fecharGrafico}
          maxWidth="xl"
          fullWidth
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            },
            '& .MuiDialog-paper': {
              width: 'calc(100% + 15%)',
              maxWidth: 'calc(1400px + 15%)',
              height: 'calc(700px + 10%)',
              maxHeight: 'calc(90vh + 10%)'
            }
          }}
          PaperProps={{
            sx: {
              height: '770px',
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            📊 Gráfico de Ocupação dos Horários por Dia da Semana
            <IconButton
              onClick={fecharGrafico}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px'
          }}>
            
            {/* Informações do Gráfico */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1
            }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                📈 Análise de Ocupação dos Horários
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Este gráfico mostra quantos colaboradores estão disponíveis em cada horário do dia, para cada dia da semana.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Como interpretar:</strong> Quanto maior a barra, mais colaboradores estão disponíveis naquele horário.
              </Typography>
            </Box>

            {/* Gráfico */}
            <Box sx={{ 
              height: '500px', 
              width: '100%',
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 1,
              p: 2
            }}>
              {dadosGrafico.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <Typography variant="h6" color="text.secondary" align="center">
                    📊 Nenhum dado disponível
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {disponibilidades.length === 0 
                      ? "Não há disponibilidades cadastradas para gerar o gráfico."
                      : "Nenhuma disponibilidade encontrada para análise."
                    }
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dadosGrafico}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="dia" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      label={{ value: 'Número de Colaboradores', angle: -90, position: 'insideLeft' }}
                      width={80}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        `${value} colaborador${value !== 1 ? 'es' : ''}`,
                        name.replace('hora_', 'Horário: ')
                      ]}
                      labelFormatter={(label) => `Dia: ${label}`}
                    />
                    <Legend />
                    {Array.from({ length: 24 }, (_, i) => (
                      <Bar 
                        key={i}
                        dataKey={`hora_${i}`}
                        name={`${i.toString().padStart(2, '0')}:00`}
                        fill={`hsl(${(i * 15) % 360}, 70%, 50%)`}
                        radius={[2, 2, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>

            {/* Resumo dos Dados */}
            {dadosGrafico.length > 0 && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                borderRadius: 1
              }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  📋 Resumo da Ocupação
                </Typography>
                <Grid container spacing={2}>
                  {dadosGrafico.map((dia, index) => {
                    const maxOcupacao = Math.max(...Array.from({ length: 24 }, (_, i) => dia[`hora_${i}`] || 0));
                    const horariosMaxOcupacao = Array.from({ length: 24 }, (_, i) => ({
                      hora: i,
                      ocupacao: dia[`hora_${i}`] || 0
                    })).filter(h => h.ocupacao === maxOcupacao && maxOcupacao > 0);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card sx={{ 
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                          border: `1px solid ${maxOcupacao > 0 ? '#4caf50' : '#ccc'}`
                        }}>
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              {dia.dia}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Máxima ocupação: {maxOcupacao} colaborador{maxOcupacao !== 1 ? 'es' : ''}
                            </Typography>
                            {horariosMaxOcupacao.length > 0 && (
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                Horários mais ocupados: {horariosMaxOcupacao.map(h => 
                                  `${h.hora.toString().padStart(2, '0')}:00`
                                ).join(', ')}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={fecharGrafico}
              >
                FECHAR
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}
